import { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import {
  X,
  Copy,
  Check,
  Link2,
  Share2,
  Download,
  MessageCircle,
  Mail,
  Facebook,
  Instagram,
  Twitter,
} from "lucide-react";
import { toast } from "sonner";

/**
 * ShareSheet — modal with every way to share a clip to social.
 *
 * On mobile, the "Share" primary button uses the native OS share sheet
 * (navigator.share) so users can post to Instagram Stories, TikTok, Signal,
 * Messages etc. without us needing OAuth. Everywhere else we fall back to
 * platform intent URLs that open the user's *already logged-in* browser
 * session on X / Facebook / WhatsApp / Reddit.
 *
 * Instagram Feed & TikTok don't accept web-share posts — for those we surface
 * a "Download for Instagram / TikTok" button so the user opens the app and
 * uploads the clean MP4 directly.
 */
export function ShareSheet({ clipId, title = "Check this out", downloadUrl, onClose }) {
  const [token, setToken] = useState(null);
  const [creating, setCreating] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .post(`/clips/${clipId}/share`)
      .then((r) => {
        if (!cancelled) setToken(r.data.token);
      })
      .catch(() => toast.error("Couldn't create a share link"))
      .finally(() => !cancelled && setCreating(false));
    return () => { cancelled = true; };
  }, [clipId]);

  const shareUrl = useMemo(
    () => (token ? `${window.location.origin}/share/${token}` : ""),
    [token],
  );

  const shareText = `${title} — watch on Rodeo Cut Studio`;

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  const doNativeShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.share({ title, text: shareText, url: shareUrl });
    } catch (_e) { /* user cancelled — silent */ }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed");
    }
  };

  const targets = useMemo(() => {
    if (!shareUrl) return [];
    const enc = encodeURIComponent;
    return [
      {
        key: "x",
        label: "X",
        icon: Twitter,
        color: "hover:text-sky-400 hover:border-sky-500",
        href: `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(shareText)}`,
      },
      {
        key: "facebook",
        label: "Facebook",
        icon: Facebook,
        color: "hover:text-blue-400 hover:border-blue-500",
        href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
      },
      {
        key: "whatsapp",
        label: "WhatsApp",
        icon: MessageCircle,
        color: "hover:text-emerald-400 hover:border-emerald-500",
        href: `https://wa.me/?text=${enc(shareText + " " + shareUrl)}`,
      },
      {
        key: "reddit",
        label: "Reddit",
        icon: Share2,
        color: "hover:text-orange-400 hover:border-orange-500",
        href: `https://reddit.com/submit?url=${enc(shareUrl)}&title=${enc(title)}`,
      },
      {
        key: "email",
        label: "Email",
        icon: Mail,
        color: "hover:text-amber-400 hover:border-amber-500",
        href: `mailto:?subject=${enc(title)}&body=${enc(shareText + "\n\n" + shareUrl)}`,
      },
    ];
  }, [shareUrl, title, shareText]);

  return (
    <div
      className="fixed inset-0 bg-black/85 z-50 flex items-end sm:items-center justify-center px-4 py-6"
      onClick={onClose}
      data-testid="share-sheet-backdrop"
    >
      <div
        className="w-full max-w-md border border-stone-800 bg-stone-950 p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-sheet"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-display uppercase font-bold text-xl">Share this clip</div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-100" data-testid="share-close">
            <X size={18} />
          </button>
        </div>

        {creating ? (
          <div className="py-10 text-center text-stone-500 text-sm">Creating public link…</div>
        ) : !shareUrl ? (
          <div className="py-10 text-center text-rose-400 text-sm">
            Couldn&apos;t create a share link. Try again.
          </div>
        ) : (
          <>
            {/* Copy link row */}
            <div className="flex items-center gap-2 border border-stone-800 p-2 mb-4">
              <Link2 size={14} className="text-stone-500 ml-1 shrink-0" />
              <div className="flex-1 min-w-0 font-mono-tc text-[11px] text-stone-300 truncate">
                {shareUrl}
              </div>
              <button
                onClick={copyLink}
                className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 shrink-0"
                data-testid="share-copy"
              >
                {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
              </button>
            </div>

            {/* Native share (mobile primary CTA) */}
            {canNativeShare && (
              <button
                onClick={doNativeShare}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm mb-4 py-3"
                data-testid="share-native"
              >
                <Share2 size={14} /> Share via your device
              </button>
            )}

            {/* Grid of platforms */}
            <div className="grid grid-cols-5 gap-2 mb-4" data-testid="share-targets">
              {targets.map((t) => (
                <a
                  key={t.key}
                  href={t.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`border border-stone-800 py-3 flex flex-col items-center gap-1 text-stone-300 transition-colors ${t.color}`}
                  data-testid={`share-${t.key}`}
                >
                  <t.icon size={16} />
                  <span className="text-[9px] uppercase tracking-widest">{t.label}</span>
                </a>
              ))}
            </div>

            {/* Manual-upload apps: Instagram & TikTok */}
            {downloadUrl && (
              <div className="border-t border-stone-800 pt-4 mt-2">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">
                  Instagram & TikTok
                </div>
                <p className="text-[11px] text-stone-500 mb-3 leading-relaxed">
                  These apps don&apos;t accept web shares. Download the clip, open the app, and upload.
                </p>
                <a
                  href={downloadUrl}
                  className="btn-secondary w-full inline-flex items-center justify-center gap-2 text-sm"
                  data-testid="share-download"
                >
                  <Download size={13} /> Download to upload manually
                </a>
                <div className="flex items-center justify-center gap-4 mt-3 text-stone-500">
                  <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest">
                    <Instagram size={11} /> Instagram
                  </div>
                  <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest">
                    <span className="font-bold">TT</span> TikTok
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
